/** Browser TTS for opening notes. Prefers neural/natural English voices. */

const PIECE: Record<string, string> = {
  N: 'knight',
  B: 'bishop',
  R: 'rook',
  Q: 'queen',
  K: 'king',
};

let generation = 0;

export function stopSpeech(): void {
  generation += 1;
  window.speechSynthesis?.cancel();
}

/** Turn SAN / squares into something a voice can say naturally. */
export function forSpeech(text: string): string {
  return text
    .replace(/\u2026/g, ', ')
    .replace(/\b([NBRQK])x([a-h])([1-8])([+#])?(?=\W|$)/g, (_, p: string, f: string, r: string, chk?: string) =>
      `${PIECE[p]} takes ${f} ${r}${checkWord(chk)}`,
    )
    .replace(/\b([NBRQK])([a-h])([1-8])([+#])?(?=\W|$)/g, (_, p: string, f: string, r: string, chk?: string) =>
      `${PIECE[p]} ${f} ${r}${checkWord(chk)}`,
    )
    .replace(/\b([a-h])([1-8])([+#])?(?=\W|$)/g, (_, f: string, r: string, chk?: string) =>
      `${f} ${r}${checkWord(chk)}`,
    )
    .replace(/\b([a-h])-file\b/gi, '$1 file')
    .replace(/\b([a-h])-pawn\b/gi, '$1 pawn')
    .replace(/\bO-O-O\b/g, 'queenside castle')
    .replace(/\bO-O\b/g, 'kingside castle');
}

function checkWord(chk?: string): string {
  if (chk === '+') return ' check';
  if (chk === '#') return ' mate';
  return '';
}

function voiceScore(v: SpeechSynthesisVoice): number {
  const name = v.name.toLowerCase();
  const lang = v.lang.toLowerCase();
  if (!lang.startsWith('en')) return -1000;

  let score = 0;
  if (/natural|neural/.test(name)) score += 120;
  if (name.includes('google')) score += 90;
  if (name.includes('online')) score += 70;
  if (/female|woman|zira|aria|jenny|ava|emma|sonia|libby|samantha|moira|tessa|karen|fiona|susan/.test(name)) {
    score -= 30;
  }
  if (/male|guy|andrew|daniel|james|george/.test(name)) {
    score += 50;
  }
  if (lang.startsWith('en-us')) score += 8;
  else if (lang.startsWith('en-gb') || lang.startsWith('en-au')) score += 5;
  if (/desktop|espeak|compact|microsoft david|microsoft zira|microsoft mark/.test(name)) {
    score -= 80;
  }
  // Old SAPI locals (David/Zira) sound robotic; neural "online" voices often report localService=false.
  if (v.localService && score < 80) score -= 25;
  return score;
}

function pickEnglishVoice(): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis.getVoices();
  const googleEn = voices.filter(
    (v) =>
      v.name.toLowerCase().includes('google') && v.lang.toLowerCase().startsWith('en'),
  );
  const named = (part: string) =>
    googleEn.find((v) => v.name.toLowerCase().includes(part));
  const notFemale = (v: SpeechSynthesisVoice) =>
    !/female|woman/.test(v.name.toLowerCase());

  // Chrome: "Google UK English Male", "Google US English" (male)
  return (
    named('uk english male') ??
    named('us english') ??
    googleEn.find((v) => /male/.test(v.name.toLowerCase()) && notFemale(v)) ??
    googleEn.find(notFemale) ??
    named('uk english') ??
    googleEn[0] ??
    bestScored(voices)
  );
}

function bestScored(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | undefined {
  let best: SpeechSynthesisVoice | undefined;
  let bestScore = -Infinity;
  for (const v of voices) {
    const score = voiceScore(v);
    if (score > bestScore) {
      bestScore = score;
      best = v;
    }
  }
  return bestScore > -1000 ? best : undefined;
}

function hasGoogleEnglish(): boolean {
  return window.speechSynthesis.getVoices().some(
    (v) =>
      v.name.toLowerCase().includes('google') && v.lang.toLowerCase().startsWith('en'),
  );
}

function whenVoicesReady(cb: () => void): void {
  if (!window.speechSynthesis) {
    cb();
    return;
  }
  if (hasGoogleEnglish()) {
    cb();
    return;
  }
  const finish = () => {
    window.speechSynthesis.removeEventListener('voiceschanged', onVoices);
    window.clearTimeout(wait);
    cb();
  };
  const onVoices = () => {
    if (hasGoogleEnglish()) finish();
  };
  window.speechSynthesis.addEventListener('voiceschanged', onVoices);
  const wait = window.setTimeout(finish, 500);
}

function speakUtterance(text: string, voice: SpeechSynthesisVoice | undefined): SpeechSynthesisUtterance {
  const utterance = new SpeechSynthesisUtterance(forSpeech(text));
  if (voice) {
    utterance.voice = voice;
    utterance.lang = voice.lang || 'en-US';
  } else {
    utterance.lang = 'en-US';
  }
  // Slightly under 1.0 reads closer to a person than the default clip.
  utterance.rate = 0.92;
  utterance.pitch = 1;
  return utterance;
}

/**
 * Speak paragraphs in order. `onIndex` receives the paragraph currently
 * being read, or null when finished / cancelled.
 * Returns a cancel function.
 */
export function speakParagraphs(
  paragraphs: readonly string[],
  onIndex: (index: number | null) => void,
): () => void {
  const my = ++generation;
  onIndex(null);

  if (!window.speechSynthesis || paragraphs.length === 0) {
    return () => {};
  }

  const cancel = (): void => {
    if (generation === my) stopSpeech();
  };

  const speakAt = (i: number, voice: SpeechSynthesisVoice | undefined): void => {
    if (generation !== my) return;
    if (i >= paragraphs.length) {
      onIndex(null);
      return;
    }
    onIndex(i);
    const utterance = speakUtterance(paragraphs[i]!, voice);
    utterance.onend = () => {
      if (generation !== my) return;
      window.setTimeout(() => speakAt(i + 1, voice), 220);
    };
    utterance.onerror = () => {
      if (generation === my) onIndex(null);
    };
    window.speechSynthesis!.speak(utterance);
  };

  whenVoicesReady(() => {
    if (generation !== my) return;
    const voice = pickEnglishVoice();
    // Chrome drops speak() if it immediately follows cancel().
    window.setTimeout(() => {
      if (generation !== my) return;
      speakAt(0, voice);
    }, 60);
  });

  return cancel;
}
